import { Box, Button, Link, Paper, TextField, Typography } from "@mui/material";
import { Theme } from "@mui/material/styles";
import { visuallyHidden } from "@mui/utils";
import { TFunction } from "i18next";
import React, { useEffect, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import { useParams } from "react-router-dom";
import { radius } from "src/design/tokens";
import { fontStretch } from "src/design/typography";
import {
  AppSwitch,
  ConfirmDialog,
  DataTable,
  EmptyState,
  FilterPills,
  InlineAlert,
  LoadingButton,
  QueryErrorAlert,
  QueryStateView,
  StatusChip,
  UploadArea,
} from "src/grading/components";
import { DataTableColumn } from "src/grading/components/DataTable/DataTable";
import { useLicenseSuspended } from "src/grading/components/LicenseBanner/LicenseBanner";
import { usePermissions } from "src/grading/hooks/usePermissions";
import {
  errorCodeCopy,
  errorCodeOf,
  errorMessage,
} from "src/grading/utils/apiError";
import { permissions } from "src/grading/utils/permissionsHelpers";
import {
  formatInTimeZone,
  isClassLinkBaseUrl,
  isRosterVendor,
  rosterSourceName,
  syncSourceKind,
} from "src/grading/utils/rosterSource";
import {
  ClassLinkAuthMode,
  RejectedRow,
  RosterCheck,
  RosterCredentialsView,
  RosterSource,
  RosterSourcePayload,
  RosterSyncRecord,
  RosterVendor,
} from "src/models/hierarchy";
import { useAppDispatch } from "src/store/hooks";
import {
  districtsApiSlice,
  rosterPollingInterval,
  useGetDistrictQuery,
  useGetRosterCredentialsQuery,
  useGetRosterStatusQuery,
  useGetSyncRejectedQuery,
  useImportRosterMutation,
  useListRosterSyncsQuery,
  usePutRosterCredentialsMutation,
  usePutRosterScheduleMutation,
  useRunRosterSyncMutation,
  useSetRosterSourceMutation,
  useTestRosterSourceMutation,
} from "src/store/reducers/districts/districtsApiSlice";
import { clearBreadcrumbs } from "src/store/reducers/navigation/navigationSlice";
import DistrictHeader from "./DistrictHeader";

type RosterFormat = "oneroster-csv" | "simple-roster";
type ProviderChoice = RosterVendor | "none";

const ACCEPT: Record<RosterFormat, string> = {
  "oneroster-csv": ".zip",
  "simple-roster": ".csv,.xlsx,.xls",
};
/** The simple-sheet template (FR-032, research R23), served from `public/templates`. */
export const ROSTER_TEMPLATE_URL = `${process.env.PUBLIC_URL ?? ""}/templates/roster-sheet-template.csv`;
/** Rejected rows shown per page; "Show more" adds another page. */
const REJECTED_PAGE = 50;

const pill = { borderRadius: `${radius.pill}px` } as const;
const card = { borderRadius: `${radius.card}px` } as const;

/** The source settings form; it re-seeds from the server only while it has no unsaved edits (FR-037). */
interface SourceForm {
  provider: ProviderChoice;
  externalId: string;
  baseUrl: string;
  authMode: ClassLinkAuthMode;
  tokenUrl: string;
}

const vendorOf = (source: RosterSource | null | undefined) => {
  const provider = source?.provider;
  return isRosterVendor(provider) ? provider : null;
};

const formOf = (source: RosterSource | null | undefined): SourceForm => ({
  provider: vendorOf(source) ?? "none",
  externalId: source?.provider === "clever" ? (source.externalId ?? "") : "",
  baseUrl: source?.provider === "classlink" ? (source.baseUrl ?? "") : "",
  authMode:
    source?.provider === "classlink" && source.authMode
      ? source.authMode
      : "oauth2",
  tokenUrl: source?.provider === "classlink" ? (source.tokenUrl ?? "") : "",
});

/** A blank form for one vendor: switching vendors never carries the previous vendor's id. */
const blankForm = (provider: ProviderChoice): SourceForm => ({
  provider,
  externalId: "",
  baseUrl: "",
  authMode: "oauth2",
  tokenUrl: "",
});

const payloadOf = (form: SourceForm): RosterSourcePayload =>
  form.provider === "clever"
    ? { provider: "clever", externalId: form.externalId.trim() }
    : {
        provider: "classlink",
        baseUrl: form.baseUrl.trim(),
        authMode: form.authMode,
        tokenUrl:
          form.authMode === "oauth2" ? form.tokenUrl.trim() || null : null,
      };

/** The newest of several connection checks. */
const newestCheck = (...checks: (RosterCheck | null | undefined)[]) =>
  checks
    .filter((c): c is RosterCheck => Boolean(c))
    .sort((a, b) => Date.parse(b.at) - Date.parse(a.at))[0] ?? null;

type ChipState = "connected" | "attention" | "unchecked";
const chipStateOf = (check: RosterCheck | null): ChipState =>
  !check ? "unchecked" : check.ok ? "connected" : "attention";

const rejectedTotalOf = (sync: RosterSyncRecord) =>
  sync.rejectedTotal ?? sync.rejectedCount;

/** True when a sync stopped without finishing (neither running nor succeeded). */
const endedInError = (sync: RosterSyncRecord) =>
  sync.status !== "running" && sync.status !== "succeeded";

/** Plain-language reason for a stopped sync: the `errorCode` copy, else the server's text. */
const stopReason = (sync: RosterSyncRecord, t: TFunction) =>
  sync.status === "interrupted"
    ? (errorCodeCopy(t, sync.errorCode) ?? t("errors.INTERRUPTED"))
    : (errorCodeCopy(t, sync.errorCode) ??
      (sync.error
        ? t("rostering.didNotComplete", { error: sync.error })
        : t("errors.UNKNOWN")));

const summaryOf = (sync: RosterSyncRecord, t: TFunction) => {
  if (sync.status === "running") return t("rostering.inProgress");
  if (endedInError(sync)) return stopReason(sync, t);
  const counts = t("rostering.syncSummary", {
    created: sync.createdCount,
    updated: sync.updatedCount,
    deactivated: sync.deactivatedCount,
    rejected: rejectedTotalOf(sync),
  });
  const skipped = sync.skipped?.deletedByRequest ?? 0;
  return skipped > 0
    ? `${counts} ${t("rostering.skippedDeleted", { count: skipped })}`
    : counts;
};

/** The result of an upload or a preview: counts, rejected total and skipped records (FR-033, FR-036). */
const SyncSummary = ({ sync }: { sync: RosterSyncRecord }) => {
  const { t } = useTranslation();
  return (
    <InlineAlert
      severity={
        endedInError(sync)
          ? "error"
          : rejectedTotalOf(sync) > 0
            ? "warning"
            : "success"
      }
    >
      {sync.dryRun ? `${t("rostering.dryRun")}: ` : ""}
      {summaryOf(sync, t)}
      {sync.dryRun && !endedInError(sync)
        ? ` ${t("rostering.nothingWritten")}`
        : ""}
      {sync.partial ? ` ${t("rostering.partialPreview")}` : ""}
    </InlineAlert>
  );
};

type RejectedRowItem = RejectedRow & { key: string };

/**
 * A sync's rejected rows, paged from `GET …/syncs/:syncId/rejected` (at most 500 are kept per
 * sync). A result with no id (an unsaved preview) shows the rows it carries.
 */
const RejectedRows = ({
  districtId,
  sync,
  testId,
  moreTestId,
}: {
  districtId: string;
  sync: RosterSyncRecord;
  testId: string;
  moreTestId: string;
}) => {
  const { t } = useTranslation();
  const [limit, setLimit] = useState(REJECTED_PAGE);
  const total = rejectedTotalOf(sync);
  const paged = useGetSyncRejectedQuery(
    { districtId, syncId: sync.id ?? "", limit, offset: 0 },
    { skip: !sync.id || total === 0 },
  );
  if (total === 0) return null;
  const inline = !sync.id;
  const items = inline
    ? (sync.rejectedRows ?? []).slice(0, limit)
    : (paged.data?.items ?? []);
  const kept = inline ? (sync.rejectedRows ?? []).length : paged.data?.total;
  const rows: RejectedRowItem[] = items.map((r, i) => ({
    ...r,
    key: `${r.file}-${r.sourcedId ?? i}-${i}`,
  }));
  const columns: DataTableColumn<RejectedRowItem>[] = [
    {
      key: "file",
      header: t("rostering.file"),
      render: (r) => r.file,
    },
    {
      key: "sourcedId",
      header: t("rostering.rowId"),
      render: (r) => r.sourcedId ?? "—",
    },
    { key: "reason", header: t("rostering.reason"), render: (r) => r.reason },
  ];

  if (!inline && paged.isError && !paged.data)
    return (
      <QueryErrorAlert query={paged}>
        {t("rostering.rejectedError")}
      </QueryErrorAlert>
    );

  return (
    <Box
      data-testid={testId}
      sx={(theme) => ({
        borderRadius: `${radius.card - 6}px`,
        bgcolor: theme.palette.surfaceSunken,
        border: `1px solid ${theme.palette.divider}`,
        overflow: "hidden",
      })}
    >
      <DataTable
        aria-label={t("rostering.rejectedRows")}
        dense
        minWidth={420}
        columns={columns}
        rows={rows}
        getRowKey={(r) => r.key}
        loading={!inline && paged.isLoading}
      />
      <Box
        sx={{
          px: 2,
          py: 1,
          display: "flex",
          gap: 1.5,
          alignItems: "center",
          flexWrap: "wrap",
        }}
      >
        <Typography sx={{ fontSize: 13 }} aria-live="polite">
          {t("rostering.rejectedShowing", { shown: rows.length, total })}
          {kept !== undefined && kept < total
            ? ` ${t("rostering.rejectedKept", { kept })}`
            : ""}
        </Typography>
        {kept !== undefined && rows.length < kept && (
          <Button
            size="small"
            data-testid={moreTestId}
            disabled={!inline && paged.isFetching}
            onClick={() => setLimit((l) => l + REJECTED_PAGE)}
            sx={{ ...pill, color: "primaryInk", fontWeight: 700 }}
          >
            {t("rostering.showMore")}
          </Button>
        )}
      </Box>
    </Box>
  );
};

const syncTone = (theme: Theme, sync: RosterSyncRecord) => {
  if (endedInError(sync))
    return { bg: theme.palette.dangerSoft, fg: theme.palette.dangerInk };
  if (sync.status === "running" || sync.dryRun || rejectedTotalOf(sync) > 0)
    return { bg: theme.palette.warning.light, fg: theme.palette.warning.dark };
  return { bg: theme.palette.success.light, fg: theme.palette.successInk };
};

const tag = {
  display: "inline-flex",
  alignItems: "center",
  px: 1.25,
  py: 0.75,
  borderRadius: `${radius.pill}px`,
  fontSize: 10,
  fontWeight: 800,
  lineHeight: 1,
  textTransform: "uppercase",
  fontStretch: fontStretch.heading,
  whiteSpace: "nowrap",
} as const;

/** Status pill for one sync: completed, running, interrupted or stopped with an error. Always text. */
const SyncStatus = ({ sync }: { sync: RosterSyncRecord }) => {
  const { t } = useTranslation();
  const label =
    sync.status === "interrupted"
      ? t("rostering.status.interrupted")
      : endedInError(sync)
        ? t("rostering.status.error")
        : sync.status === "running"
          ? t("rostering.status.running")
          : t("rostering.status.completed");
  return (
    <Box sx={{ display: "inline-flex", gap: 0.75, flexWrap: "wrap" }}>
      <Box
        component="span"
        data-sync-status={sync.status}
        sx={(theme) => {
          const { bg, fg } = syncTone(theme, sync);
          return { ...tag, bgcolor: bg, color: fg };
        }}
      >
        {label}
      </Box>
      {sync.dryRun && (
        <Box
          component="span"
          sx={(theme) => ({
            ...tag,
            bgcolor: theme.palette.surfaceAlt,
            color: theme.palette.text.primary,
          })}
        >
          {t("rostering.dryRun")}
        </Box>
      )}
    </Box>
  );
};

/** Native select with a visible label, so it works with keyboards, screen readers and the e2e suite. */
const SelectField = ({
  label,
  value,
  onChange,
  testId,
  children,
  helperText,
  disabled,
}: {
  label: string;
  value: string;
  onChange: (value: string) => void;
  testId: string;
  children: React.ReactNode;
  helperText?: string;
  disabled?: boolean;
}) => (
  <TextField
    select
    label={label}
    value={value}
    disabled={disabled}
    onChange={(e) => onChange(e.target.value)}
    helperText={helperText}
    SelectProps={{ native: true }}
    inputProps={{ "data-testid": testId }}
  >
    {children}
  </TextField>
);

type ResultKind = "upload" | "preview";

/**
 * Rostering (contracts/console-district-tabs.md, `rostering`): connect Clever or ClassLink (with
 * write-only credentials), test and disconnect the connection, run or preview a sync, pause the
 * nightly schedule, upload a OneRoster bundle or roster sheet, and follow the sync history.
 */
const RosteringPage = () => {
  const { t, i18n } = useTranslation();
  const dispatch = useAppDispatch();
  const { districtId } = useParams<{ districtId: string }>();
  // Read without manage (a suspended license, R19): everything stays visible, nothing changes.
  const { hasPermissions } = usePermissions();
  const canManage = hasPermissions(
    permissions.MANAGE_ROSTERING.action,
    permissions.MANAGE_ROSTERING.subject,
  );
  const readOnly = !canManage;
  const ownLicenseSuspended = useLicenseSuspended();

  const districtQuery = useGetDistrictQuery(districtId);
  const district = districtQuery.data;
  const source = district?.rosterSource ?? null;
  const savedVendor: RosterVendor | null = vendorOf(source);

  // FR-031: the history and status refresh every 5 s only while a sync is running.
  const [poll, setPoll] = useState(0);
  const statusQuery = useGetRosterStatusQuery(districtId, {
    pollingInterval: poll,
  });
  const syncsQuery = useListRosterSyncsQuery(districtId, {
    pollingInterval: poll,
  });
  const status = statusQuery.data;
  const syncs = syncsQuery.data;
  const interval = rosterPollingInterval(syncs, status);
  useEffect(() => {
    setPoll(interval);
  }, [interval]);
  const running =
    status?.running ?? (syncs ?? []).find((s) => s.status === "running");
  const isRunning = Boolean(running);

  // A background sync changes the district's schools, classes and students when it ends.
  const wasRunning = useRef(isRunning);
  useEffect(() => {
    if (wasRunning.current && !isRunning)
      dispatch(
        districtsApiSlice.util.invalidateTags([
          { type: "Districts", id: districtId },
          { type: "Schools", id: districtId },
          "Classes",
          "Students",
        ]),
      );
    wasRunning.current = isRunning;
  }, [isRunning, dispatch, districtId]);

  const [importRoster, { isLoading: importing }] = useImportRosterMutation();
  const [setSource, { isLoading: savingSource }] = useSetRosterSourceMutation();
  const [runSync] = useRunRosterSyncMutation();
  const [testSource, { isLoading: testing }] = useTestRosterSourceMutation();
  const [putSchedule, { isLoading: savingSchedule }] =
    usePutRosterScheduleMutation();
  const [putCredentials, { isLoading: savingCredentials }] =
    usePutRosterCredentialsMutation();

  // Source form (FR-037): re-seeded from the server only while it has no unsaved edits.
  const [form, setForm] = useState<SourceForm>(() => formOf(source));
  const dirty = useRef(false);
  const sourceKey = JSON.stringify(formOf(source));
  useEffect(() => {
    if (!dirty.current) setForm(JSON.parse(sourceKey) as SourceForm);
  }, [sourceKey]);
  const [baseUrlTouched, setBaseUrlTouched] = useState(false);
  const [sourceError, setSourceError] = useState<string | null>(null);
  const [baseUrlRefused, setBaseUrlRefused] = useState(false);
  const [sourceSaved, setSourceSaved] = useState(false);
  const edit = (patch: Partial<SourceForm>) => {
    dirty.current = true;
    setSourceSaved(false);
    setSourceError(null);
    setForm((f) => ({ ...f, ...patch }));
  };

  // Write-only ClassLink credentials (R5): the secret is never shown and is cleared once saved.
  const showCredentials =
    form.provider === "classlink" || savedVendor === "classlink";
  const credentialsQuery = useGetRosterCredentialsQuery(districtId, {
    skip: !showCredentials,
  });
  const [savedCredentials, setSavedCredentials] =
    useState<RosterCredentialsView | null>(null);
  const credentials = savedCredentials ?? credentialsQuery.data ?? null;
  const [clientId, setClientId] = useState("");
  const clientIdEdited = useRef(false);
  const serverClientId = credentialsQuery.data?.clientId ?? "";
  useEffect(() => {
    if (!clientIdEdited.current) setClientId(serverClientId);
  }, [serverClientId]);
  const [clientSecret, setClientSecret] = useState("");
  const [credentialsError, setCredentialsError] = useState<string | null>(null);

  // Connection test: the newest of the server's check and the one just run updates the chip (R26).
  const [localCheck, setLocalCheck] = useState<RosterCheck | null>(null);
  const [testError, setTestError] = useState<string | null>(null);
  const check = newestCheck(status?.lastCheck, source?.lastCheck, localCheck);
  const chip = chipStateOf(check);

  const [disconnectOpen, setDisconnectOpen] = useState(false);
  const [disconnectError, setDisconnectError] = useState<string | null>(null);

  // Sync now and Preview each have their own busy state (FR-031).
  const [busy, setBusy] = useState<"sync" | "preview" | null>(null);
  const [syncStarted, setSyncStarted] = useState(false);
  const [scheduleError, setScheduleError] = useState<string | null>(null);

  // Upload.
  const [uploadKey, setUploadKey] = useState(0);
  const [file, setFile] = useState<File | null>(null);
  const [format, setFormat] = useState<RosterFormat>("oneroster-csv");
  const [dryRun, setDryRun] = useState(true);
  const [result, setResult] = useState<{
    kind: ResultKind;
    sync: RosterSyncRecord;
  } | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [expanded, setExpanded] = useState<string | null>(null);

  // Every district tab shares the district header and its tabs, so none shows a breadcrumb trail
  // (it overlapped the header's eyebrow).
  useEffect(() => {
    dispatch(clearBreadcrumbs());
    return () => {
      dispatch(clearBreadcrumbs());
    };
  }, [dispatch]);

  const clearMessages = () => {
    setError(null);
    setResult(null);
    setSyncStarted(false);
  };

  const changeFormat = (next: RosterFormat) => {
    // A different format takes a different file: drop the chosen one and its result.
    setFormat(next);
    setFile(null);
    setUploadKey((k) => k + 1);
    clearMessages();
  };

  const upload = async () => {
    if (!file) return;
    clearMessages();
    try {
      const sync = await importRoster({
        districtId,
        file,
        format,
        dryRun,
      }).unwrap();
      setResult({ kind: "upload", sync });
      if (!dryRun) {
        setFile(null);
        setUploadKey((k) => k + 1);
      }
    } catch (e) {
      setError(errorMessage(t, e, t("rostering.importError")));
    }
  };

  const baseUrlInvalid =
    form.provider === "classlink" &&
    (baseUrlRefused || (baseUrlTouched && !isClassLinkBaseUrl(form.baseUrl)));
  const formComplete =
    form.provider === "clever"
      ? Boolean(form.externalId.trim())
      : form.provider === "classlink"
        ? isClassLinkBaseUrl(form.baseUrl)
        : false;

  const saveSource = async () => {
    if (form.provider === "none") return;
    setSourceError(null);
    setSourceSaved(false);
    setBaseUrlRefused(false);
    try {
      const saved = await setSource({
        districtId,
        ...payloadOf(form),
      }).unwrap();
      dirty.current = false;
      setForm(formOf(saved ?? null));
      setBaseUrlTouched(false);
      setLocalCheck(null);
      setSourceSaved(true);
    } catch (e) {
      if (errorCodeOf(e) === "INVALID_BASE_URL") setBaseUrlRefused(true);
      setSourceError(errorMessage(t, e, t("rostering.sourceError")));
    }
  };

  const switchVendor = (next: RosterVendor) => {
    // FR-037: the previous vendor's id never carries over.
    dirty.current = true;
    setForm(blankForm(next));
    setBaseUrlTouched(false);
    setBaseUrlRefused(false);
    setSourceSaved(false);
    setSourceError(null);
  };

  const disconnect = async () => {
    setDisconnectError(null);
    try {
      await setSource({ districtId, provider: null }).unwrap();
      dirty.current = false;
      setForm(blankForm("none"));
      setLocalCheck(null);
      setSavedCredentials(null);
      setDisconnectOpen(false);
    } catch (e) {
      setDisconnectError(errorMessage(t, e, t("rostering.disconnectError")));
    }
  };

  const saveCredentials = async () => {
    setCredentialsError(null);
    try {
      const saved = await putCredentials({
        districtId,
        clientId: clientId.trim(),
        clientSecret,
      }).unwrap();
      setSavedCredentials(saved);
      clientIdEdited.current = false;
      setClientSecret("");
    } catch (e) {
      setCredentialsError(errorMessage(t, e, t("rostering.credentialsError")));
    }
  };

  const runTest = async () => {
    setTestError(null);
    try {
      setLocalCheck(await testSource(districtId).unwrap());
    } catch (e) {
      setTestError(errorMessage(t, e, t("rostering.testError")));
    }
  };

  const syncNow = async (dry: boolean) => {
    clearMessages();
    setBusy(dry ? "preview" : "sync");
    try {
      const started = await runSync({ districtId, dryRun: dry }).unwrap();
      if (started.sync) setResult({ kind: "preview", sync: started.sync });
      else setSyncStarted(true);
    } catch (e) {
      setError(errorMessage(t, e, t("rostering.syncError")));
    } finally {
      setBusy(null);
    }
  };

  const paused = status?.schedule.paused ?? source?.schedule?.paused ?? false;
  const toggleSchedule = async (resume: boolean) => {
    setScheduleError(null);
    try {
      await putSchedule({ districtId, paused: !resume }).unwrap();
    } catch (e) {
      setScheduleError(errorMessage(t, e, t("rostering.scheduleError")));
    }
  };

  const timeZone = district?.timezone ?? null;
  const nextRunAt = status?.schedule.nextRunAt ?? null;
  const nextRunText = !savedVendor
    ? t("rostering.nextRunNone")
    : paused
      ? t("rostering.schedulePaused")
      : nextRunAt
        ? t("rostering.nextRun", {
            date: formatInTimeZone(nextRunAt, timeZone, i18n.language),
          })
        : t("rostering.nextRunUnknown");

  const syncBlocked =
    readOnly || !savedVendor || isRunning || importing || busy !== null;

  const historyColumns: DataTableColumn<RosterSyncRecord>[] = [
    {
      key: "when",
      header: t("rostering.when"),
      render: (s) => (
        <Box component="span" sx={{ fontWeight: 700 }}>
          {new Date(s.startedAt).toLocaleString(i18n.language)}
        </Box>
      ),
    },
    {
      key: "provider",
      header: t("rostering.source"),
      render: (s) => (
        <Box component="span" sx={{ color: "text.secondary" }}>
          {rosterSourceName(t, syncSourceKind(s.provider))}
        </Box>
      ),
    },
    {
      key: "status",
      header: t("rostering.statusHeader"),
      render: (s) => <SyncStatus sync={s} />,
    },
    {
      key: "summary",
      header: t("rostering.summary"),
      render: (s) => summaryOf(s, t),
    },
    {
      key: "rejected",
      header: (
        <Box component="span" sx={visuallyHidden}>
          {t("rostering.rejectedRows")}
        </Box>
      ),
      align: "right",
      render: (s) =>
        s.id && rejectedTotalOf(s) > 0 ? (
          <Button
            size="small"
            data-testid="history-expand"
            aria-expanded={expanded === s.id}
            aria-controls="roster-rejected"
            onClick={() =>
              setExpanded(expanded === s.id ? null : (s.id ?? null))
            }
            sx={{ ...pill, color: "primaryInk", fontWeight: 700 }}
          >
            {t("rostering.rejectedCount", { count: rejectedTotalOf(s) })}
          </Button>
        ) : null,
    },
  ];

  const resultState = (sync: RosterSyncRecord) =>
    endedInError(sync)
      ? "upload-invalid"
      : rejectedTotalOf(sync) > 0
        ? "upload-partial"
        : "upload-valid";

  const renderPage = () => {
    if (!district) return null;
    const d = district;
    const otherVendor: RosterVendor | null = savedVendor
      ? savedVendor === "clever"
        ? "classlink"
        : "clever"
      : null;
    const expandedSync = (syncs ?? []).find((s) => s.id === expanded);
    const lastSyncAt = status?.lastSyncAt ?? source?.lastSyncAt ?? null;
    const credentialsReady = Boolean(clientId.trim() && clientSecret);

    return (
      <Box sx={{ display: "flex", flexDirection: "column", gap: 3 }}>
        <DistrictHeader districtId={districtId} district={d} />

        {readOnly && (
          <Box data-testid="rostering-read-only">
            <InlineAlert severity="info" title={t("rostering.readOnlyTitle")}>
              {ownLicenseSuspended || d.licenseStatus === "suspended"
                ? t("rostering.readOnlySuspended")
                : t("rostering.readOnlyBody")}
            </InlineAlert>
          </Box>
        )}

        <Box
          sx={{
            display: "grid",
            gridTemplateColumns: "minmax(0, 1fr)",
            gap: 3,
            alignItems: "stretch",
            "@media (min-width: 1024px)": {
              gridTemplateColumns: "repeat(2, minmax(0, 1fr))",
            },
          }}
        >
          <Paper
            component="section"
            aria-labelledby="roster-vendor"
            sx={{
              ...card,
              p: 3,
              display: "flex",
              flexDirection: "column",
              gap: 2,
            }}
          >
            <Box
              sx={{
                display: "flex",
                alignItems: "center",
                justifyContent: "space-between",
                gap: 2,
                flexWrap: "wrap",
              }}
            >
              <Typography id="roster-vendor" variant="h4" component="h2">
                {t("rostering.connectedSource")}
              </Typography>
              {savedVendor && (
                <Box
                  component="span"
                  data-testid="status-chip"
                  data-state={chip}
                  role="status"
                >
                  <StatusChip status={chip} />
                </Box>
              )}
            </Box>

            {savedVendor ? (
              <Box>
                <Typography
                  sx={{ fontWeight: 800, fontSize: 16, lineHeight: 1.3 }}
                >
                  {t(`rostering.vendorName.${savedVendor}`)}
                </Typography>
                <Typography
                  sx={{
                    fontSize: 13,
                    color: "text.secondary",
                    overflowWrap: "anywhere",
                  }}
                >
                  {savedVendor === "clever"
                    ? source?.externalId
                      ? t("rostering.cleverIdValue", { id: source.externalId })
                      : null
                    : source?.baseUrl}
                </Typography>
              </Box>
            ) : (
              <Typography
                data-testid="no-source"
                sx={{ fontSize: 14, color: "text.secondary" }}
              >
                {t("rostering.noVendor")}
              </Typography>
            )}

            {savedVendor && chip === "attention" && check && (
              <InlineAlert severity="warning">
                {t("rostering.checkProblem", {
                  reason:
                    errorCodeCopy(t, check.errorCode) ?? t("errors.UNKNOWN"),
                })}
              </InlineAlert>
            )}
            {localCheck && (
              <Typography
                data-testid="test-result"
                role="status"
                sx={{ fontSize: 13.5, color: "text.secondary" }}
              >
                {localCheck.ok
                  ? t("rostering.testOk", {
                      date: new Date(localCheck.at).toLocaleString(
                        i18n.language,
                      ),
                    })
                  : t("rostering.testProblem", {
                      date: new Date(localCheck.at).toLocaleString(
                        i18n.language,
                      ),
                    })}
              </Typography>
            )}
            {testError && (
              <InlineAlert severity="error">{testError}</InlineAlert>
            )}
            {statusQuery.isError && !status && (
              <QueryErrorAlert query={statusQuery}>
                {t("rostering.statusError")}
              </QueryErrorAlert>
            )}

            {lastSyncAt && savedVendor && (
              <Typography sx={{ fontSize: 13.5, color: "text.secondary" }}>
                {t("rostering.lastSync", {
                  date: new Date(lastSyncAt).toLocaleString(i18n.language),
                })}
              </Typography>
            )}

            <Box
              sx={{
                display: "flex",
                alignItems: "center",
                justifyContent: "space-between",
                gap: 1.5,
                flexWrap: "wrap",
              }}
            >
              <AppSwitch
                checked={Boolean(savedVendor) && !paused}
                disabled={readOnly || !savedVendor || savingSchedule}
                onChange={(e) => toggleSchedule(e.target.checked)}
                label={t("rostering.scheduleToggle")}
                inputProps={
                  {
                    "data-testid": "schedule-toggle",
                  } as React.InputHTMLAttributes<HTMLInputElement>
                }
              />
              <Typography
                data-testid="next-run"
                data-state={paused ? "schedule-paused" : undefined}
                sx={{ fontSize: 13.5, color: "text.secondary" }}
              >
                {nextRunText}
              </Typography>
            </Box>
            {scheduleError && (
              <InlineAlert severity="error">{scheduleError}</InlineAlert>
            )}

            <Box sx={{ display: "flex", gap: 1.5, flexWrap: "wrap" }}>
              <LoadingButton
                variant="contained"
                color="secondary"
                data-testid="sync-now"
                loading={busy === "sync"}
                disabled={syncBlocked}
                onClick={() => syncNow(false)}
                label={t("rostering.syncNow")}
                sx={pill}
              />
              <LoadingButton
                variant="outlined"
                color="secondary"
                data-testid="preview-sync"
                loading={busy === "preview"}
                disabled={syncBlocked}
                onClick={() => syncNow(true)}
                label={t("rostering.previewSync")}
                sx={pill}
              />
              <LoadingButton
                variant="outlined"
                color="secondary"
                data-testid="test-connection"
                loading={testing}
                disabled={readOnly || !savedVendor}
                onClick={runTest}
                label={t("rostering.testConnection")}
                sx={pill}
              />
              {otherVendor && !readOnly && (
                <Button
                  variant="outlined"
                  color="secondary"
                  data-testid="switch-vendor"
                  onClick={() => switchVendor(otherVendor)}
                  sx={{
                    ...pill,
                    borderWidth: 1.5,
                    "&:hover": { borderWidth: 1.5 },
                  }}
                >
                  {t("rostering.switchTo", {
                    provider: rosterSourceName(t, otherVendor),
                  })}
                </Button>
              )}
              {savedVendor && !readOnly && (
                <Button
                  variant="text"
                  color="error"
                  data-testid="disconnect"
                  onClick={() => {
                    setDisconnectError(null);
                    setDisconnectOpen(true);
                  }}
                  sx={pill}
                >
                  {t("rostering.disconnect")}
                </Button>
              )}
            </Box>
            {!savedVendor ? (
              <Typography sx={{ fontSize: 12.5, color: "text.secondary" }}>
                {t("rostering.noSourceNote")}
              </Typography>
            ) : isRunning ? (
              <Typography
                data-state="sync-running"
                role="status"
                sx={{ fontSize: 12.5, color: "text.secondary" }}
              >
                {t("rostering.syncRunningNote")}
              </Typography>
            ) : null}

            {!readOnly && (
              <Box
                component="form"
                aria-labelledby="roster-source-form"
                noValidate
                onSubmit={(e: React.FormEvent) => {
                  e.preventDefault();
                  void saveSource();
                }}
                sx={(theme) => ({
                  display: "flex",
                  flexDirection: "column",
                  gap: 2,
                  pt: 2,
                  borderTop: `1px solid ${theme.palette.divider}`,
                })}
              >
                <Typography id="roster-source-form" variant="h5" component="h3">
                  {t("rostering.sourceSettings")}
                </Typography>
                <SelectField
                  label={t("rostering.provider")}
                  value={form.provider}
                  testId="source-provider"
                  onChange={(v) => {
                    const next = v as ProviderChoice;
                    dirty.current = true;
                    setForm(
                      next === savedVendor ? formOf(source) : blankForm(next),
                    );
                    setBaseUrlTouched(false);
                    setBaseUrlRefused(false);
                    setSourceSaved(false);
                    setSourceError(null);
                  }}
                  helperText={
                    form.provider === "none" && savedVendor
                      ? t("rostering.useDisconnect")
                      : undefined
                  }
                >
                  <option value="none">{t("rostering.providerNone")}</option>
                  <option value="clever">
                    {t("rostering.vendorName.clever")}
                  </option>
                  <option value="classlink">
                    {t("rostering.vendorName.classlink")}
                  </option>
                </SelectField>

                {form.provider === "clever" && (
                  <TextField
                    label={t("rostering.cleverId")}
                    value={form.externalId}
                    required
                    onChange={(e) => edit({ externalId: e.target.value })}
                    helperText={t("rostering.cleverIdHelper")}
                    inputProps={{ "data-testid": "clever-district-id" }}
                  />
                )}
                {form.provider === "classlink" && (
                  <>
                    <TextField
                      label={t("rostering.classlinkBaseUrl")}
                      value={form.baseUrl}
                      required
                      type="url"
                      onChange={(e) => {
                        setBaseUrlRefused(false);
                        edit({ baseUrl: e.target.value });
                      }}
                      onBlur={() => setBaseUrlTouched(true)}
                      error={baseUrlInvalid}
                      helperText={
                        baseUrlInvalid
                          ? t("errors.INVALID_BASE_URL")
                          : t("rostering.classlinkBaseUrlHelper")
                      }
                      inputProps={{ "data-testid": "classlink-base-url" }}
                    />
                    <SelectField
                      label={t("rostering.classlinkAuthMode")}
                      value={form.authMode}
                      testId="classlink-auth-mode"
                      onChange={(v) =>
                        edit({ authMode: v as ClassLinkAuthMode })
                      }
                    >
                      <option value="oauth2">
                        {t("rostering.authMode.oauth2")}
                      </option>
                      <option value="oauth1">
                        {t("rostering.authMode.oauth1")}
                      </option>
                    </SelectField>
                    {form.authMode === "oauth2" && (
                      <TextField
                        label={t("rostering.classlinkTokenUrl")}
                        value={form.tokenUrl}
                        type="url"
                        onChange={(e) => edit({ tokenUrl: e.target.value })}
                        helperText={t("rostering.classlinkTokenUrlHelper")}
                        inputProps={{ "data-testid": "classlink-token-url" }}
                      />
                    )}
                  </>
                )}
                {sourceError && !baseUrlRefused && (
                  <InlineAlert severity="error">{sourceError}</InlineAlert>
                )}
                {sourceSaved && (
                  <InlineAlert severity="success">
                    {t("rostering.sourceSaved")}
                  </InlineAlert>
                )}
                <div>
                  <LoadingButton
                    type="submit"
                    variant="outlined"
                    data-testid="source-save"
                    loading={savingSource && !disconnectOpen}
                    disabled={!formComplete}
                    label={t("rostering.saveSource")}
                    sx={pill}
                  />
                </div>
              </Box>
            )}

            {showCredentials && (
              <Box
                component={readOnly ? "div" : "form"}
                aria-labelledby="roster-credentials"
                noValidate={readOnly ? undefined : true}
                autoComplete={readOnly ? undefined : "off"}
                onSubmit={(e: React.FormEvent) => {
                  e.preventDefault();
                  if (credentialsReady) void saveCredentials();
                }}
                sx={(theme) => ({
                  display: "flex",
                  flexDirection: "column",
                  gap: 2,
                  pt: 2,
                  borderTop: `1px solid ${theme.palette.divider}`,
                })}
              >
                <Typography id="roster-credentials" variant="h5" component="h3">
                  {t("rostering.credentialsTitle")}
                </Typography>
                {/* FR-048: a failed or pending read never says "No credentials saved yet". */}
                {credentialsQuery.isError && !credentials ? (
                  <QueryErrorAlert
                    query={credentialsQuery}
                    data-testid="credentials-load-error"
                  >
                    {t("rostering.credentialsLoadError")}
                  </QueryErrorAlert>
                ) : (
                  <Typography
                    data-testid="credentials-status"
                    role={credentials ? undefined : "status"}
                    sx={{ fontSize: 13, color: "text.secondary" }}
                  >
                    {credentials?.configured
                      ? credentials.updatedByName && credentials.updatedAt
                        ? t("rostering.credentialsSavedBy", {
                            name: credentials.updatedByName,
                            date: new Date(
                              credentials.updatedAt,
                            ).toLocaleString(i18n.language),
                          })
                        : t("rostering.credentialsSaved")
                      : credentials
                        ? t("rostering.credentialsNone")
                        : t("rostering.credentialsLoading")}
                  </Typography>
                )}
                {!readOnly && (
                  <>
                    <TextField
                      label={t("rostering.clientId")}
                      value={clientId}
                      autoComplete="off"
                      onChange={(e) => {
                        clientIdEdited.current = true;
                        setClientId(e.target.value);
                      }}
                      inputProps={{ "data-testid": "credentials-client-id" }}
                    />
                    <TextField
                      label={t("rostering.clientSecret")}
                      value={clientSecret}
                      type="password"
                      autoComplete="new-password"
                      onChange={(e) => setClientSecret(e.target.value)}
                      helperText={t("rostering.clientSecretHelper")}
                      inputProps={{ "data-testid": "credentials-secret" }}
                    />
                    {credentialsError && (
                      <InlineAlert severity="error">
                        {credentialsError}
                      </InlineAlert>
                    )}
                    <div>
                      <LoadingButton
                        type="submit"
                        variant="outlined"
                        data-testid="credentials-save"
                        loading={savingCredentials}
                        disabled={!credentialsReady}
                        label={t("rostering.saveCredentials")}
                        sx={pill}
                      />
                    </div>
                  </>
                )}
              </Box>
            )}
          </Paper>

          <Paper
            component="section"
            aria-labelledby="roster-upload"
            sx={{
              ...card,
              p: 3,
              display: "flex",
              flexDirection: "column",
              gap: 2,
            }}
          >
            <Typography id="roster-upload" variant="h4" component="h2">
              {t("rostering.uploadTitle")}
            </Typography>
            {readOnly ? (
              <Typography
                data-testid="upload-read-only"
                sx={{ fontSize: 13.5, color: "text.secondary" }}
              >
                {t("rostering.readOnlyUpload")}
              </Typography>
            ) : (
              <>
                <FilterPills
                  aria-label={t("rostering.format")}
                  data-testid="upload-format"
                  size="sm"
                  variant="trayAlt"
                  value={format}
                  onChange={(v) => changeFormat(v as RosterFormat)}
                  options={[
                    {
                      value: "oneroster-csv",
                      label: t("rostering.formatOneRoster"),
                    },
                    {
                      value: "simple-roster",
                      label: t("rostering.formatSheet"),
                    },
                  ]}
                />
                <Typography sx={{ fontSize: 12.5, color: "text.secondary" }}>
                  {t("rostering.privacyNote", {
                    dob: d.privacySettings?.shareDob
                      ? t("rostering.dobStored")
                      : t("rostering.dobConverted"),
                    guardians:
                      d.privacySettings?.letterDelivery === "email"
                        ? t("rostering.guardiansImported")
                        : t("rostering.guardiansSkipped"),
                  })}
                </Typography>
                {format === "simple-roster" && (
                  <Box
                    sx={{ display: "flex", flexDirection: "column", gap: 0.75 }}
                  >
                    <Typography variant="body2" color="text.secondary">
                      {t("rostering.sheetColumns")}
                    </Typography>
                    <Link
                      href={ROSTER_TEMPLATE_URL}
                      download="roster-sheet-template.csv"
                      data-testid="template-download"
                      sx={{
                        fontWeight: 700,
                        color: "primaryInk",
                        alignSelf: "flex-start",
                      }}
                    >
                      {t("rostering.templateDownload")}
                    </Link>
                  </Box>
                )}
                <UploadArea
                  key={uploadKey}
                  accept={ACCEPT[format]}
                  inputTestId="upload-file"
                  disabled={importing}
                  hint={
                    format === "simple-roster"
                      ? t("rostering.uploadHintSheet")
                      : t("rostering.uploadHintOneRoster")
                  }
                  onFile={(f) => {
                    setFile(f);
                    clearMessages();
                  }}
                  onClear={() => setFile(null)}
                />
                <AppSwitch
                  checked={dryRun}
                  onChange={(e) => setDryRun(e.target.checked)}
                  label={t("rostering.dryRunNote")}
                  inputProps={
                    {
                      "data-testid": "upload-dry-run",
                    } as React.InputHTMLAttributes<HTMLInputElement>
                  }
                />
                <div>
                  <LoadingButton
                    variant="contained"
                    data-testid="upload-submit"
                    loading={importing}
                    disabled={!file || isRunning || busy !== null}
                    onClick={upload}
                    label={
                      dryRun ? t("rostering.validate") : t("rostering.import")
                    }
                    sx={pill}
                  />
                </div>
              </>
            )}
          </Paper>
        </Box>

        {(error || result || syncStarted) && (
          <Box
            component="section"
            aria-live="polite"
            aria-label={t("rostering.resultTitle")}
            sx={{ display: "flex", flexDirection: "column", gap: 2 }}
          >
            {error && <InlineAlert severity="error">{error}</InlineAlert>}
            {syncStarted && (
              <InlineAlert severity="info">
                <span data-testid="sync-started">
                  {t("rostering.syncStarted")}
                </span>
              </InlineAlert>
            )}
            {result && (
              <Box
                data-testid={
                  result.kind === "upload" ? "upload-result" : "preview-result"
                }
                data-state={resultState(result.sync)}
                sx={{ display: "flex", flexDirection: "column", gap: 2 }}
              >
                <SyncSummary sync={result.sync} />
                <RejectedRows
                  districtId={districtId}
                  sync={result.sync}
                  testId="rejected-rows"
                  moreTestId="rejected-rows-more"
                />
              </Box>
            )}
          </Box>
        )}

        <Paper
          component="section"
          aria-labelledby="roster-history"
          sx={{ ...card, overflow: "hidden" }}
        >
          <Typography
            id="roster-history"
            variant="h4"
            component="h2"
            sx={{ px: 3, py: 2 }}
          >
            {t("rostering.history")}
          </Typography>
          <Box
            sx={{
              px: syncsQuery.isError && !syncs ? 3 : 0,
              pb: syncsQuery.isError && !syncs ? 3 : 0,
            }}
          >
            <QueryStateView
              query={syncsQuery}
              headingLevel={3}
              errorMessage={t("rostering.historyError")}
            >
              <DataTable
                aria-label={t("rostering.history")}
                columns={historyColumns}
                rows={syncs ?? []}
                getRowKey={(s) => s.id ?? s.startedAt}
                getRowData={(s) => ({
                  "data-testid": "history-table-row",
                  "data-status": s.status,
                })}
                minWidth={720}
                emptyState={
                  <Box data-testid="history-empty">
                    <EmptyState
                      headingLevel={3}
                      title={t("rostering.noHistory")}
                    />
                  </Box>
                }
              />
            </QueryStateView>
          </Box>
          {expandedSync && (
            <Box
              id="roster-rejected"
              sx={{
                px: 3,
                pb: 3,
                display: "flex",
                flexDirection: "column",
                gap: 1.5,
              }}
            >
              <Typography variant="overline" component="h3">
                {t("rostering.rejectedRows")}
                {" · "}
                {new Date(expandedSync.startedAt).toLocaleString(i18n.language)}
              </Typography>
              {endedInError(expandedSync) && (
                <InlineAlert severity="error">
                  {stopReason(expandedSync, t)}
                </InlineAlert>
              )}
              <RejectedRows
                key={expandedSync.id}
                districtId={districtId}
                sync={expandedSync}
                testId="history-rejected"
                moreTestId="history-rejected-more"
              />
            </Box>
          )}
        </Paper>

        <ConfirmDialog
          open={disconnectOpen}
          tone="destructive"
          testIdPrefix="disconnect"
          title={t("rostering.disconnectTitle", {
            provider: savedVendor ? rosterSourceName(t, savedVendor) : "",
          })}
          body={t("rostering.disconnectBody")}
          confirmLabel={t("rostering.disconnect")}
          loading={savingSource}
          onConfirm={disconnect}
          onClose={() => setDisconnectOpen(false)}
        >
          {disconnectError ? (
            <InlineAlert severity="error">{disconnectError}</InlineAlert>
          ) : null}
        </ConfirmDialog>
      </Box>
    );
  };

  return <QueryStateView query={districtQuery}>{renderPage}</QueryStateView>;
};

export default RosteringPage;
